import React, { useState } from "react";
import {
  Star,
  Eye,
  X,
  MoreHorizontal,
  Copy,
  Archive,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import QuestionPreviewModal from "./QuestionPreviewModal";

const QuestionDetailPanel = ({
  question,
  onClose,
  onEdit,
  onToggleFavourite,
  onDuplicate,
  onArchive,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const text = question.questionText || "—";
  const type = question.questionType || "—";
  const category = question.category || "—";
  const isFavourite = question.isFavourite ?? false;
  const options = question.options || [];
  const isRequired = question.required ?? false;
  const usedIn = question.usedIn || [];
  const isArchived = question.isArchived ?? false;

  const minValue = question.minValue;
  const maxValue = question.maxValue;
  const minLabel = question.minLabel;
  const maxLabel = question.maxLabel;

  const handleDuplicate = () => {
    setMenuOpen(false);
    onDuplicate?.(question);
    onClose?.();
  };
  const handleArchive = () => {
    setMenuOpen(false);
    onArchive?.(question._id);
  };
  const handleDeleteClick = () => {
    setMenuOpen(false);
    setConfirmDelete(true);
  };
  const handleDeleteConfirm = () => {
    setConfirmDelete(false);
    onDelete?.(question._id);
    onClose?.();
  };

  return (
    <div
      className="flex flex-col h-full rounded-lg overflow-hidden
                         border border-gray-200 dark:border-gray-700
                         bg-white dark:bg-gray-800"
    >
      {/* ── Top Icons ── */}
      <div className="flex items-center justify-between p-4 pb-3">
        <button
          onClick={() => onToggleFavourite?.(question._id)}
          className="p-0.5"
        >
          <Star
            className={cn(
              "h-5 w-5 transition-colors",
              isFavourite
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300 hover:text-yellow-400 dark:text-gray-600 dark:hover:text-yellow-400",
            )}
          />
        </button>

        <div className="flex items-center gap-3">
          <Eye
            className="h-5 w-5 cursor-pointer transition-colors
                                   text-gray-400 hover:text-primary
                                   dark:text-gray-500 dark:hover:text-primary"
            onClick={() => setPreviewOpen(true)}
          />
          <X
            className="h-5 w-5 cursor-pointer transition-colors
                                   text-gray-400 hover:text-primary
                                   dark:text-gray-500 dark:hover:text-primary"
            onClick={onClose}
          />
        </div>
      </div>

      <Separator className="dark:bg-gray-700" />

      {/* ── Scrollable Content ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* ── Question Text ── */}
        <p
          className="text-sm font-semibold leading-snug
                               text-gray-800 dark:text-gray-100"
        >
          {text}
        </p>

        {/* ── Category + Type Badges ── */}
        <div className="flex flex-wrap gap-2">
          <Badge
            variant="secondary"
            className="text-xs uppercase tracking-wide font-semibold
                                   dark:bg-gray-700 dark:text-gray-300"
          >
            {category}
          </Badge>

          <span
            className="rounded-full px-2 py-1 text-[11px] font-semibold capitalize
                                     bg-primary/10 text-primary
                                     dark:bg-primary/20 dark:text-primary"
          >
            {type?.replace(/_/g, " ") || "text"}
          </span>

          {isArchived && (
            <Badge
              variant="outline"
              className="text-xs text-orange-500 border-orange-300
                                       dark:border-orange-500/50 dark:text-orange-400"
            >
              Archived
            </Badge>
          )}
        </div>

        <Separator className="dark:bg-gray-700" />

        {/* ── Answer Options ── */}
        {options.length > 0 && (
          <>
            <div className="space-y-2">
              <p
                className="text-[11px] font-semibold uppercase tracking-widest
                                           text-muted-foreground dark:text-gray-500"
              >
                Answer Type
              </p>
              <ul className="space-y-1.5">
                {options.map((opt, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-gray-700 dark:text-gray-300"
                  >
                    {opt?.label || opt}
                  </li>
                ))}
              </ul>
            </div>
            <Separator className="dark:bg-gray-700" />
          </>
        )}

        {/* ── Scale Range ── */}
        {type === "scale" &&
          (minValue !== undefined || maxValue !== undefined) && (
            <>
              <div className="space-y-2">
                <p
                  className="text-[11px] font-semibold uppercase tracking-widest
                                           text-muted-foreground dark:text-gray-500"
                >
                  Scale Range
                </p>
                <div
                  className="flex items-center gap-2 text-sm
                                             text-gray-700 dark:text-gray-300"
                >
                  <span>{minLabel || minValue}</span>
                  <span className="text-muted-foreground dark:text-gray-500">
                    →
                  </span>
                  <span>{maxLabel || maxValue}</span>
                </div>
                <div className="text-xs text-muted-foreground dark:text-gray-500">
                  {minValue} – {maxValue}
                </div>
              </div>
              <Separator className="dark:bg-gray-700" />
            </>
          )}

        {/* ── Default Setting ── */}
        <div className="space-y-2">
          <p
            className="text-[11px] font-semibold uppercase tracking-widest
                                   text-muted-foreground dark:text-gray-500"
          >
            Default Setting
          </p>
          <div className="flex flex-wrap gap-2">
            {isRequired ? (
              <Badge
                variant="secondary"
                className="text-xs dark:bg-gray-700 dark:text-gray-300"
              >
                Required
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-xs text-muted-foreground
                                           dark:border-gray-600 dark:text-gray-500"
              >
                Optional
              </Badge>
            )}
          </div>
        </div>

        <Separator className="dark:bg-gray-700" />

        {/* ── Used In ── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p
              className="text-[11px] font-semibold uppercase tracking-widest
                                       text-muted-foreground dark:text-gray-500"
            >
              Used In
            </p>
            {usedIn.length > 0 && (
              <span
                className="text-[11px] font-medium rounded-full px-1.5 py-0.5
                                             bg-muted text-muted-foreground
                                             dark:bg-gray-700 dark:text-gray-400"
              >
                {usedIn.length}
              </span>
            )}
          </div>

          {usedIn.length > 0 ? (
            <ul className="space-y-1.5">
              {usedIn.map((survey, idx) => (
                <li
                  key={survey?.surveyId || idx}
                  className="text-sm cursor-pointer transition-colors hover:underline
                                               text-emerald-600 hover:text-emerald-700
                                               dark:text-emerald-400 dark:hover:text-emerald-300"
                >
                  {survey?.surveyName || "Untitled Survey"}
                </li>
              ))}
            </ul>
          ) : (
            <p
              className="text-sm italic
                                       text-muted-foreground dark:text-gray-500"
            >
              Not used in any surveys yet.
            </p>
          )}
        </div>
      </div>

      {/* ── Delete Confirmation ── */}
      {confirmDelete && (
        <div
          className="px-4 py-3 space-y-2 border-t
                                 bg-red-50 border-red-100
                                 dark:bg-red-950/40 dark:border-red-900/50"
        >
          <p
            className="text-sm font-medium
                                   text-red-700 dark:text-red-400"
          >
            Delete this question?
          </p>
          <p className="text-xs text-red-500 dark:text-red-500">
            This action cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 dark:border-gray-600 dark:text-gray-300
                                       dark:hover:bg-gray-700"
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white
                                       dark:bg-red-700 dark:hover:bg-red-600"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div
        className="p-4 flex items-center gap-2
                             border-t border-gray-200 dark:border-gray-700"
      >
        <Button className="flex-1" onClick={() => onEdit?.(question)}>
          Edit question
        </Button>

        {/* ── More Menu ── */}
        <div className="relative shrink-0">
          <Button
            variant="outline"
            size="icon"
            className="dark:border-gray-600 dark:text-gray-300
                                   dark:hover:bg-gray-700"
            onClick={() => setMenuOpen((p) => !p)}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>

          {menuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setMenuOpen(false)}
              />

              {/* Dropdown */}
              <div
                className="absolute bottom-10 right-0 z-20 w-44 rounded-lg
                                             shadow-lg py-1 border
                                             bg-white border-gray-200
                                             dark:bg-gray-800 dark:border-gray-700"
              >
                <button
                  onClick={handleDuplicate}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm
                                               transition-colors
                                               text-gray-700 hover:bg-gray-50
                                               dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Copy
                    className="h-4 w-4
                                                     text-gray-400 dark:text-gray-500"
                  />
                  Duplicate
                </button>

                <button
                  onClick={handleArchive}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm
                                               transition-colors
                                               text-gray-700 hover:bg-gray-50
                                               dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Archive
                    className="h-4 w-4
                                                        text-gray-400 dark:text-gray-500"
                  />
                  {isArchived ? "Unarchive" : "Archive"}
                </button>

                <Separator className="dark:bg-gray-700" />

                <button
                  onClick={handleDeleteClick}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm
                                               transition-colors
                                               text-red-600 hover:bg-red-50
                                               dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <QuestionPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        question={question}
      />
    </div>
  );
};

export default QuestionDetailPanel;
